import React, { useState } from 'react';
import { X, Sparkles, Loader2, Volume2, Copy, Check, BookmarkPlus } from 'lucide-react';
import { apiClient } from '../services/api';
import { logCardReview } from '../utils/reviewUtils';
import { trackAIGenerated } from '../utils/analytics';
import { speak } from '../utils/speechUtils';
import { useAuth } from '../hooks/useAuth';
import { getLanguage, getMicLocale } from '../utils/language';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface GeneratePhrasesModalProps {
  cardId: number;
  cardName: string;
  onClose: () => void;
  onAddPhrase?: (phrase: string) => Promise<void>;
}

export const GeneratePhrasesModal: React.FC<GeneratePhrasesModalProps> = ({ cardId, cardName, onClose, onAddPhrase }) => {
  useBodyScrollLock();
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [phrases, setPhrases] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const language = getLanguage(user?.email ?? '');
  const micLocale = getMicLocale(user?.email ?? '');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [addedIndices, setAddedIndices] = useState<Set<number>>(new Set());
  const [addingIndex, setAddingIndex] = useState<number | null>(null);

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);
    setPhrases([]);
    try {
      const result = await apiClient.generatePhrases(cardId, {
        prompt: prompt.trim() || undefined,
        language,
      });
      setPhrases(result.phrases);
      logCardReview(cardId);
      trackAIGenerated('phrases', cardId);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate phrases. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleListen = (phrase: string) => {
    speak(phrase, { lang: micLocale });
  };

  const handleCopy = (phrase: string, index: number) => {
    navigator.clipboard.writeText(phrase);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  const handleAdd = async (phrase: string, index: number) => {
    if (!onAddPhrase || addedIndices.has(index)) return;
    setAddingIndex(index);
    try {
      await onAddPhrase(phrase);
      setAddedIndices(prev => new Set(prev).add(index));
    } finally {
      setAddingIndex(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Generate phrases</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              <span className="font-medium text-gray-700">&quot;{cardName}&quot;</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Prompt <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={`e.g. "Give me 5 formal phrases with '${cardName}'"`}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/80 focus:border-purple-300 resize-none bg-white text-sm"
              rows={2}
            />
          </div>

          <button
            type="button"
            onClick={handleGenerate}
            disabled={isLoading}
            className="w-full px-4 py-3 text-[15px] font-medium bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-md hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-sm"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5" />
                Generate
              </>
            )}
          </button>

          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{error}</div>
          )}

          {phrases.length > 0 && (
            <ul className="space-y-3">
              {phrases.map((phrase, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                >
                  <p className="flex-1 text-sm text-gray-800 leading-relaxed">{phrase}</p>
                  <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                    <button
                      type="button"
                      onClick={() => handleListen(phrase)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                      title="Listen"
                    >
                      <Volume2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopy(phrase, i)}
                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                      title="Copy"
                    >
                      {copiedIndex === i ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                    {onAddPhrase && (
                      <button
                        type="button"
                        onClick={() => handleAdd(phrase, i)}
                        disabled={addedIndices.has(i) || addingIndex === i}
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-full transition-colors disabled:cursor-not-allowed"
                        title={addedIndices.has(i) ? 'Added to My Phrases' : 'Add to My Phrases'}
                      >
                        {addingIndex === i ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : addedIndices.has(i) ? (
                          <Check className="h-4 w-4 text-amber-500" />
                        ) : (
                          <BookmarkPlus className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
